/**
 * 统一错误类型与 Result 包装。
 *
 * 约定（ARCH §8.2）：所有 service 只允许抛 `MeshLinkError`，
 * UI 只消费 `errorCatalog[code]` 的人话文案。
 *
 * 说明：`MeshLinkError` 类本体定义在 `@shared/result`（见其文件头的分层约束），
 * 此处**再导出**，业务侧仍从 `@/lib/result` 导入、路径不变。
 */

import { isErrorCode, type ErrorCode } from '@shared/errors';
import { MeshLinkError } from '@shared/result';

export { MeshLinkError };

export type Result<T> = { ok: true; value: T } | { ok: false; error: MeshLinkError };

/** 把任意异常规整为 MeshLinkError（未知异常回落 E_UNKNOWN）。 */
export function toMeshLinkError(err: unknown, fallback: ErrorCode = 'E_UNKNOWN'): MeshLinkError {
  if (err instanceof MeshLinkError) return err;
  if (err instanceof Error && isErrorCode(err.message)) {
    return new MeshLinkError(err.message, undefined, err);
  }
  return new MeshLinkError(fallback, undefined, err);
}

/** 同步包装：永不抛异常。 */
export function tryCatch<T>(fn: () => T, fallback: ErrorCode = 'E_UNKNOWN'): Result<T> {
  try {
    return { ok: true, value: fn() };
  } catch (err) {
    return { ok: false, error: toMeshLinkError(err, fallback) };
  }
}

/** 异步包装：永不 reject。 */
export async function tryCatchAsync<T>(fn: () => Promise<T>, fallback: ErrorCode = 'E_UNKNOWN'): Promise<Result<T>> {
  try {
    return { ok: true, value: await fn() };
  } catch (err) {
    return { ok: false, error: toMeshLinkError(err, fallback) };
  }
}

/** 断言式守卫：条件不满足即抛指定错误码。 */
export function assert(condition: unknown, code: ErrorCode, message?: string): asserts condition {
  if (!condition) throw new MeshLinkError(code, message);
}
